import React, { useState, useRef, useMemo, useEffect } from 'react';
import { parseManifest } from '../../services/geminiService';
import { saveVessel, generateId, getUniqueManifestValues } from '../../services/storageService';
import { Vessel, BitumenGrade, PortName, ImportRecord } from '../../types';
import { 
  FileText, Save, CheckCircle, AlertCircle, Loader2, Upload, X, 
  File as FileIcon, Trash2, Plus, MapPin, ArrowUp, ArrowDown, 
  ArrowUpDown, Keyboard, Sparkles, Info, Calculator, 
  ArrowRightCircle, Trash, MousePointer2, Settings2, Database,
  User, Building2, Layers, Hash, Anchor, ClipboardList, StickyNote
} from 'lucide-react';
import StandardizationModal from './StandardizationModal';

export const FINAL_DESTINATIONS = [
  "Tanzania",
  "Malawi",
  "DRC",
  "Zambia",
  "Rwanda",
  "Burundi"
];

interface SmartEntryProps {
  onSuccess: () => void;
  currentPort: PortName;
}

interface SaveConfirmationModalProps {
  isOpen: boolean;
  data: any;
  currentPort: string;
  onCancel: () => void;
  onConfirm: () => void;
}

const SaveConfirmationModal: React.FC<SaveConfirmationModalProps> = ({ isOpen, data, currentPort, onCancel, onConfirm }) => {
  if (!isOpen || !data) return null;

  const totalQty = data.imports.reduce((acc: number, item: any) => acc + (Number(item.quantityMetricTons) || 0), 0);
  const itemCount = data.imports.length;
  const uniqueNotify = new Set(data.imports.map((i: any) => i.notifyParty).filter(Boolean)).size;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
        <div className="bg-white rounded-[40px] shadow-2xl max-w-md w-full p-8 animate-in zoom-in-95 duration-200 border border-slate-200">
            <div className="text-center mb-8">
                <div className="w-20 h-20 bg-emerald-50 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-sm">
                    <Save className="text-emerald-600 w-10 h-10" />
                </div>
                <h3 className="text-2xl font-black text-slate-800 uppercase tracking-tight">Audit Checklist</h3>
                <p className="text-sm text-slate-500 font-medium mt-1">Reviewing registry payload for {currentPort}.</p>
            </div>

            <div className="bg-slate-50 rounded-3xl p-6 mb-8 space-y-4 border border-slate-100 shadow-inner">
                <div className="flex justify-between text-[10px] font-black text-slate-400 uppercase tracking-widest">
                    <span>Vessel Identity</span>
                    <span className="text-slate-800">{data.vesselName || 'TBD'}</span>
                </div>
                <div className="flex justify-between text-[10px] font-black text-slate-400 uppercase tracking-widest">
                    <span>Registry Date</span>
                    <span className="text-slate-800">{data.arrivalDate}</span>
                </div>
                 <div className="border-t border-slate-200 my-2"></div>
                <div className="flex justify-between text-[10px] font-black text-slate-400 uppercase tracking-widest">
                    <span>B/L Count</span>
                    <span className="text-slate-800">{itemCount} Records</span>
                </div>
                <div className="flex justify-between text-[10px] font-black text-slate-400 uppercase tracking-widest">
                    <span>Notify Parties</span>
                    <span className="text-indigo-600">{uniqueNotify} Unique</span>
                </div>
                <div className="flex justify-between text-base font-black text-slate-900 pt-2">
                    <span className="uppercase tracking-tighter">Total Tonnage</span>
                    <span className="text-indigo-600">{totalQty.toLocaleString()} MT</span>
                </div>
            </div>

            <div className="flex gap-4">
                <button 
                    onClick={onCancel}
                    className="flex-1 px-6 py-4 text-slate-500 hover:bg-slate-50 rounded-2xl font-black text-[10px] uppercase tracking-widest transition-all"
                >
                    Discard
                </button>
                <button 
                    onClick={onConfirm}
                    className="flex-1 px-6 py-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] shadow-xl shadow-indigo-100 transition-all active:scale-95"
                >
                    Final Commit
                </button>
            </div>
        </div>
    </div>
  );
};

export const SmartEntry: React.FC<SmartEntryProps> = ({ onSuccess, currentPort }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [suggestions, setSuggestions] = useState<{ importers: string[], suppliers: string[], notifyParties: string[] }>({ importers: [], suppliers: [], notifyParties: [] });
  const [assignedPort, setAssignedPort] = useState<PortName>(currentPort);
  
  const [showStandardizationModal, setShowStandardizationModal] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const loadSuggestions = async () => {
        const [importers, suppliers, notifyParties] = await Promise.all([
            getUniqueManifestValues('importerName'),
            getUniqueManifestValues('supplier'),
            getUniqueManifestValues('notifyParty')
        ]);
        setSuggestions({ importers, suppliers, notifyParties });
    };
    loadSuggestions();
  }, []);

  useEffect(() => {
    setAssignedPort(currentPort);
  }, [currentPort]);

  const totalQuantity = useMemo(() => {
    if (!parsedData?.imports) return 0;
    return parsedData.imports.reduce((sum: number, item: any) => sum + (Number(item.quantityMetricTons) || 0), 0);
  }, [parsedData]);

  const availablePorts = useMemo(() => {
    if (currentPort === PortName.MOMBASA) return [PortName.MOMBASA];
    return [PortName.DAR_ES_SALAAM, PortName.TANGA, PortName.ZANZIBAR];
  }, [currentPort]);

  const validateAndProcess = (file: File) => {
    if (file.type === 'application/pdf') {
        setSelectedFile(file);
        setError(null);
        handleProcessFile(file);
    } else {
        setError("Unsupported format. Please upload a PDF manifest.");
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      validateAndProcess(e.target.files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
        validateAndProcess(e.dataTransfer.files[0]);
    }
  };

  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  };

  const handleStartManual = () => {
    setParsedData({
        vesselName: '',
        imo: '',
        importerName: '',
        arrivalDate: new Date().toISOString().split('T')[0],
        imports: [
            { importerName: '', supplier: '', notifyParty: '', finalDestination: FINAL_DESTINATIONS[0], quantityMetricTons: 0, grade: BitumenGrade.G_60_70, notes: '', blNumber: '' }
        ]
    });
    setError(null);
  };

  const handleProcessFile = async (file: File) => {
    setIsProcessing(true);
    setError(null);
    setParsedData(null);

    try {
      const base64Data = await fileToBase64(file);
      const result = await parseManifest({
        mimeType: file.type,
        data: base64Data
      });
      
      if (result && result.imports) {
        result.imports = result.imports.map((item: any) => ({
            ...item,
            supplier: item.supplier || '',
            notifyParty: item.notifyParty || '',
            // Map AI suggestion to standardized destinations if possible, otherwise default to Tanzania
            finalDestination: FINAL_DESTINATIONS.find(d => d.toUpperCase() === item.finalDestination?.toUpperCase()) || FINAL_DESTINATIONS[0],
            notes: '',
            blNumber: item.blNumber || ''
        }));
      }
      setParsedData(result);
    } catch (err) {
      console.error(err);
      setError("AI Engine Handshake Failed. Verify API Key configuration.");
      setSelectedFile(null);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleVesselChange = (field: string, value: string) => {
    if (!parsedData) return;
    setParsedData({ ...parsedData, [field]: value });
  };

  const handleUpdateImport = (index: number, field: string, value: any) => {
    if (!parsedData) return;
    const newImports = [...parsedData.imports];
    newImports[index] = { ...newImports[index], [field]: value };
    setParsedData({ ...parsedData, imports: newImports });
  };

  const handleDeleteImport = (index: number) => {
    if (!parsedData) return;
    if (parsedData.imports.length === 1) {
        handleUpdateImport(0, 'importerName', '');
        handleUpdateImport(0, 'quantityMetricTons', 0);
        return;
    }
    const newImports = parsedData.imports.filter((_: any, i: number) => i !== index);
    setParsedData({ ...parsedData, imports: newImports });
  };

  const handleAddImport = () => {
    if (!parsedData) return;
    const newImport = { importerName: parsedData.importerName || '', supplier: '', notifyParty: '', finalDestination: FINAL_DESTINATIONS[0], quantityMetricTons: 0, grade: BitumenGrade.G_60_70, notes: '', blNumber: '' };
    setParsedData({ ...parsedData, imports: [...parsedData.imports, newImport] });
  };

  const executeSave = async () => {
    if (!parsedData) return;

    const newVessel: Vessel = {
      id: generateId(),
      name: (parsedData.vesselName || 'UNKNOWN VESSEL').toUpperCase(),
      imo: parsedData.imo || '',
      arrivalDate: parsedData.arrivalDate || new Date().toISOString().split('T')[0],
      status: 'Expected',
      port: assignedPort,
      manifest: parsedData.imports.map((imp: any) => ({
        id: generateId(),
        vesselName: (parsedData.vesselName || 'UNKNOWN VESSEL').toUpperCase(),
        importerName: (imp.importerName || parsedData.importerName || 'UNKNOWN IMPORTER').toUpperCase(),
        quantityMetricTons: Number(imp.quantityMetricTons) || 0,
        quantity: Number(imp.quantityMetricTons) || 0,
        grade: imp.grade || BitumenGrade.G_60_70,
        bitumenGrade: imp.grade || BitumenGrade.G_60_70,
        supplier: (imp.supplier || '').toUpperCase(),
        notifyParty: (imp.notifyParty || '').toUpperCase(),
        finalDestination: imp.finalDestination,
        arrivalDate: parsedData.arrivalDate || new Date().toISOString().split('T')[0],
        notes: imp.notes || `Input via manual registry entry`,
        blNumber: imp.blNumber || ''
      }))
    };

    await saveVessel(newVessel);
    setShowConfirmation(false);
    onSuccess();
  };

  return (
    <div className="flex flex-col h-full bg-white rounded-[40px] shadow-sm border border-slate-200 overflow-hidden">
      <StandardizationModal 
        isOpen={showStandardizationModal}
        onClose={() => setShowStandardizationModal(false)}
        data={parsedData}
        onApply={(updatedData) => {
            setParsedData(updatedData);
            setShowStandardizationModal(false);
            setShowConfirmation(true);
        }}
      />

      <SaveConfirmationModal 
        isOpen={showConfirmation}
        data={parsedData}
        currentPort={assignedPort}
        onCancel={() => setShowConfirmation(false)}
        onConfirm={executeSave}
      />

      {!parsedData ? (
        <div className="flex-1 flex flex-col">
            <div className="p-8 bg-slate-50 border-b border-slate-200 flex justify-between items-center shrink-0">
              <div>
                <h3 className="text-xl font-black text-slate-800 uppercase tracking-tight flex items-center gap-3 text-lg">
                  <div className="p-3 bg-white rounded-2xl shadow-sm border border-slate-100 text-indigo-600">
                    <Upload size={24} />
                  </div>
                  Registry Ingestion
                </h3>
                <p className="text-sm text-slate-500 font-medium mt-1">Select an entry method to begin manifest reconciliation.</p>
              </div>
            </div>

            <div className="flex-1 p-12 flex items-center justify-center">
              {isProcessing ? (
                <div className="flex flex-col items-center justify-center space-y-6 animate-in fade-in">
                    <div className="relative">
                        <div className="w-24 h-24 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin"></div>
                        <Sparkles className="absolute inset-0 m-auto text-indigo-500 animate-pulse" size={32} />
                    </div>
                    <div className="text-center">
                        <h4 className="text-lg font-black text-slate-800 tracking-tight uppercase">Synchronizing Intelligence...</h4>
                        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em] mt-2">Gemini 3 Flash Extraction in Progress</p>
                    </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 w-full max-w-6xl">
                    <div 
                      onClick={() => fileInputRef.current?.click()}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={handleDrop}
                      className={`group relative bg-white p-10 rounded-[3rem] border-2 border-dashed transition-all cursor-pointer flex flex-col items-center text-center space-y-6 overflow-hidden ${isDragging ? 'border-indigo-500 bg-indigo-50 scale-[1.02] shadow-2xl' : 'border-slate-200 hover:border-indigo-400 hover:bg-indigo-50/30'}`}
                    >
                        <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                            <FileIcon size={120} className="text-indigo-600" />
                        </div>
                        <div className={`w-20 h-20 rounded-[2rem] flex items-center justify-center shadow-sm transition-all duration-300 ${isDragging ? 'bg-indigo-600 text-white scale-110 rotate-3' : 'bg-indigo-50 text-indigo-600 group-hover:scale-110'}`}>
                            <Upload size={36} />
                        </div>
                        <div>
                            <h4 className="font-black text-2xl text-slate-800 uppercase tracking-tight">PDF Scan</h4>
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] mt-2">{isDragging ? 'DROP TO ANALYZE' : 'AI-Powered OCR Extraction'}</p>
                        </div>
                        <p className="text-sm text-slate-500 font-medium leading-relaxed">
                            {isDragging ? 'Release the file to begin processing.' : 'Upload binary PDF manifests. The Nexus AI will automatically extract importers, quantities, and vessel identities.'}
                        </p>
                        <input 
                            type="file" 
                            accept="application/pdf" 
                            ref={fileInputRef}
                            className="hidden" 
                            onChange={handleFileChange} 
                        />
                    </div>

                    <div 
                      onClick={handleStartManual}
                      className="group relative bg-white p-10 rounded-[3rem] border-2 border-dashed border-slate-200 hover:border-emerald-400 hover:bg-emerald-50/30 transition-all cursor-pointer flex flex-col items-center text-center space-y-6 overflow-hidden"
                    >
                        <div className="absolute top-0 right-0 p-4 opacity-5 group-hover:opacity-10 transition-opacity">
                            <Keyboard size={120} className="text-emerald-600" />
                        </div>
                        <div className="w-20 h-20 bg-emerald-50 rounded-[2rem] text-emerald-600 flex items-center justify-center shadow-sm group-hover:scale-110 transition-transform">
                            <MousePointer2 size={36} />
                        </div>
                        <div>
                            <h4 className="font-black text-2xl text-slate-800 uppercase tracking-tight">Manual Entry</h4>
                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.3em] mt-2">Direct Spreadsheet Input</p>
                        </div>
                        <p className="text-sm text-slate-500 font-medium leading-relaxed">
                            Initialize a clean manifest ledger. Manually input line-by-line data for immediate registry commitment.
                        </p>
                    </div>
                </div>
              )}
            </div>

            {error && (
              <div className="mx-12 mb-12 p-4 bg-rose-50 border border-rose-100 text-rose-600 text-xs font-bold rounded-2xl flex items-center gap-3 animate-in slide-in-from-top-2">
                  <AlertCircle size={18} /> {error}
              </div>
            )}
        </div>
      ) : (
        <div className="flex-1 flex flex-col overflow-hidden relative">
          <header className="p-6 bg-slate-50 border-b border-slate-200 flex justify-between items-center shrink-0 z-30 shadow-sm">
              <div className="flex items-center gap-4">
                 <button onClick={() => { setParsedData(null); setSelectedFile(null); }} className="p-3 text-slate-400 hover:text-rose-500 transition-colors bg-white rounded-xl border border-slate-200 shadow-sm" title="Clear Workspace">
                    <X size={20} />
                 </button>
                 <div>
                    <h3 className="font-black text-slate-800 uppercase tracking-tight flex items-center gap-3 text-lg">
                        <div className="p-2 bg-white rounded-xl shadow-sm border border-slate-100 text-emerald-500">
                            <FileText size={20} />
                        </div>
                        Workspace Editor
                    </h3>
                    <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mt-1">{parsedData.imports.length} active consignments</p>
                 </div>
              </div>
              <div className="flex items-center gap-4">
                  <div className="text-right border-r border-slate-200 pr-5 mr-1 hidden md:block">
                      <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Aggregate Payload (MT)</p>
                      <p className="text-2xl font-black text-slate-800 tracking-tight">{totalQuantity.toLocaleString()}</p>
                  </div>
                  <button 
                    onClick={() => setShowStandardizationModal(true)}
                    className="bg-emerald-600 text-white px-8 py-3.5 rounded-2xl font-black text-[11px] uppercase tracking-[0.2em] shadow-xl shadow-emerald-100 hover:bg-emerald-700 transition-all flex items-center gap-3 active:scale-95"
                  >
                    <Database size={18} /> Commit Registry
                  </button>
              </div>
          </header>

          <div className="flex-1 overflow-auto custom-scrollbar bg-slate-50/40">
              <div className="p-6 space-y-6 w-full mx-auto">
                 <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 bg-slate-900 p-6 rounded-[2rem] shadow-xl relative overflow-hidden shrink-0 border border-white/5">
                    <div className="absolute top-0 right-0 w-64 h-64 bg-indigo-500/10 blur-3xl -mr-20 -mt-20"></div>
                    <div className="space-y-1 relative z-10">
                        <label className="text-[9px] font-black text-indigo-400 uppercase tracking-widest ml-1">Vessel Call Name</label>
                        <input 
                            type="text" 
                            value={parsedData.vesselName} 
                            onChange={e => handleVesselChange('vesselName', e.target.value.toUpperCase())}
                            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm font-black text-white focus:ring-4 focus:ring-indigo-500/30 outline-none transition-all uppercase"
                            placeholder="VESSEL NAME"
                        />
                    </div>
                    <div className="space-y-1 relative z-10">
                        <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">IMO Identity</label>
                        <input 
                          type="text" 
                          value={parsedData.imo} 
                          onChange={e => handleVesselChange('imo', e.target.value)}
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm font-mono text-slate-300 focus:ring-4 focus:ring-indigo-500/30 outline-none transition-all"
                          placeholder="0000000"
                        />
                    </div>
                    <div className="space-y-1 relative z-10">
                        <label className="text-[9px] font-black text-indigo-400 uppercase tracking-widest ml-1">Primary Importer</label>
                        <input 
                            type="text" 
                            value={parsedData.importerName || ''} 
                            onChange={e => handleVesselChange('importerName', e.target.value.toUpperCase())}
                            className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm font-black text-white focus:ring-4 focus:ring-indigo-500/30 outline-none transition-all uppercase"
                            list="importer-suggestions"
                            placeholder="IMPORTER ENTITY"
                        />
                    </div>
                    <div className="space-y-1 relative z-10">
                        <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Arrival Stamp</label>
                        <input 
                          type="date" 
                          value={parsedData.arrivalDate} 
                          onChange={e => handleVesselChange('arrivalDate', e.target.value)}
                          className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm font-bold text-slate-300 focus:ring-4 focus:ring-indigo-500/30 outline-none transition-all appearance-none cursor-pointer"
                        />
                    </div>
                    <div className="space-y-1 relative z-10">
                        <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">Assigned Port</label>
                        <div className="relative">
                          <select 
                              value={assignedPort}
                              onChange={(e) => setAssignedPort(e.target.value as PortName)}
                              className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-sm font-black text-white focus:ring-4 focus:ring-indigo-500/30 outline-none transition-all uppercase appearance-none cursor-pointer"
                          >
                              {availablePorts.map(p => (
                                  <option key={p} value={p} className="bg-slate-900 text-white">{p}</option>
                              ))}
                          </select>
                          <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-indigo-400">
                             <MapPin size={16} />
                          </div>
                        </div>
                    </div>
                 </div>

                 <div className="space-y-4">
                    <div className="p-4 bg-slate-100/50 border-b border-slate-200 flex justify-between items-center rounded-2xl mb-4">
                        <div className="flex items-center gap-3">
                            <Settings2 size={16} className="text-indigo-500" />
                            <h4 className="text-[10px] font-black text-slate-800 uppercase tracking-[0.2em]">Consignment Records</h4>
                        </div>
                        <button 
                            onClick={handleAddImport}
                            className="px-4 py-2 bg-indigo-600 text-white hover:bg-indigo-700 rounded-xl text-[9px] font-black uppercase tracking-widest transition-all flex items-center gap-2 shadow-md"
                        >
                            <Plus size={14} /> Add Entry
                        </button>
                    </div>

                    <div className="grid grid-cols-1 gap-4">
                        {parsedData.imports.map((item: any, idx: number) => (
                            <div key={idx} className="bg-white rounded-[2rem] border border-slate-200 shadow-sm hover:shadow-lg hover:border-indigo-300 transition-all p-6 relative group overflow-hidden">
                                <div className="flex justify-between items-center mb-4 pb-3 border-b border-slate-50">
                                    <div className="flex items-center gap-2">
                                        <div className="w-8 h-8 bg-indigo-50 rounded-lg flex items-center justify-center text-indigo-600 font-black text-[10px] border border-indigo-100">
                                            {idx + 1}
                                        </div>
                                        <h5 className="font-black text-slate-500 uppercase tracking-widest text-[9px]">Consignment {idx + 1}</h5>
                                    </div>
                                    <button 
                                        onClick={() => handleDeleteImport(idx)} 
                                        className="text-slate-300 hover:text-rose-500 transition-colors p-1.5 hover:bg-rose-50 rounded-lg"
                                    >
                                        <Trash2 size={18} />
                                    </button>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-4">
                                    <div className="space-y-1">
                                        <label className="flex items-center gap-2 text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">
                                            <Building2 size={10} className="text-indigo-400" /> Importer
                                        </label>
                                        <input 
                                            type="text" 
                                            value={item.importerName} 
                                            onChange={e => handleUpdateImport(idx, 'importerName', e.target.value.toUpperCase())}
                                            className={`w-full bg-slate-50 border border-slate-200 focus:border-indigo-500 focus:bg-white focus:ring-4 focus:ring-indigo-500/10 outline-none font-bold text-slate-900 transition-all px-3 py-2 rounded-xl text-xs ${!item.importerName ? 'border-rose-200 bg-rose-50/5' : ''}`}
                                            list="importer-suggestions"
                                            placeholder="IMPORTER NAME..."
                                        />
                                    </div>

                                    <div className="space-y-1">
                                        <label className="flex items-center gap-2 text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">
                                            <User size={10} className="text-slate-300" /> Notify Party
                                        </label>
                                        <input 
                                            type="text" 
                                            value={item.notifyParty} 
                                            onChange={e => handleUpdateImport(idx, 'notifyParty', e.target.value.toUpperCase())}
                                            className="w-full bg-slate-50 border border-slate-200 focus:border-indigo-500 focus:bg-white focus:ring-4 focus:ring-indigo-500/10 outline-none text-slate-600 font-medium px-3 py-2 rounded-xl text-xs"
                                            list="notify-suggestions"
                                            placeholder="NOTIFY PARTY..."
                                        />
                                    </div>

                                    <div className="space-y-1">
                                        <label className="flex items-center gap-2 text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">
                                            <Anchor size={10} className="text-indigo-400" /> Supplier
                                        </label>
                                        <input 
                                            type="text" 
                                            value={item.supplier} 
                                            onChange={e => handleUpdateImport(idx, 'supplier', e.target.value.toUpperCase())}
                                            className="w-full bg-slate-50 border border-slate-200 focus:border-indigo-500 focus:bg-white focus:ring-4 focus:ring-indigo-500/10 outline-none text-slate-800 font-bold px-3 py-2 rounded-xl text-xs"
                                            list="supplier-suggestions"
                                            placeholder="SUPPLIER ENTITY..."
                                        />
                                    </div>

                                    <div className="space-y-1">
                                        <label className="flex items-center gap-2 text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">
                                            <Hash size={10} className="text-indigo-400" /> BL Number
                                        </label>
                                        <input 
                                            type="text" 
                                            value={item.blNumber} 
                                            onChange={e => handleUpdateImport(idx, 'blNumber', e.target.value.toUpperCase())}
                                            className="w-full bg-slate-50 border border-slate-200 focus:border-indigo-500 focus:bg-white focus:ring-4 focus:ring-indigo-500/10 outline-none font-mono text-xs font-black text-indigo-600 tracking-tighter px-3 py-2 rounded-xl"
                                            placeholder="BL REFERENCE..."
                                        />
                                    </div>

                                    <div className="space-y-1">
                                        <label className="flex items-center gap-2 text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">
                                            <Layers size={10} className="text-indigo-400" /> Grade
                                        </label>
                                        <select 
                                            value={item.grade} 
                                            onChange={e => handleUpdateImport(idx, 'grade', e.target.value)}
                                            className="w-full bg-indigo-50 border border-indigo-100 text-indigo-700 font-black text-[10px] px-3 py-2 rounded-xl uppercase outline-none"
                                        >
                                            {Object.values(BitumenGrade).map(g => <option key={g} value={g}>{g}</option>)}
                                        </select>
                                    </div>

                                    <div className="space-y-1">
                                        <label className="flex items-center gap-2 text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">
                                            <Calculator size={10} className="text-emerald-500" /> Qty (MT)
                                        </label>
                                        <input 
                                            type="number" 
                                            value={item.quantityMetricTons || ''} 
                                            onChange={e => handleUpdateImport(idx, 'quantityMetricTons', e.target.value)}
                                            className={`w-full bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-right font-black text-slate-900 focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 outline-none transition-all text-xs ${!item.quantityMetricTons ? 'border-rose-200 bg-rose-50/5' : ''}`}
                                            placeholder="0.00"
                                        />
                                    </div>

                                    <div className="space-y-1">
                                        <label className="flex items-center gap-2 text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">
                                            <MapPin size={10} className="text-indigo-400" /> Final Destination
                                        </label>
                                        <select 
                                            value={item.finalDestination} 
                                            onChange={e => handleUpdateImport(idx, 'finalDestination', e.target.value)}
                                            className="w-full bg-slate-50 border border-slate-200 focus:border-indigo-500 focus:bg-white focus:ring-4 focus:ring-indigo-500/10 outline-none text-slate-700 font-bold px-3 py-2 rounded-xl text-xs appearance-none cursor-pointer"
                                        >
                                            {FINAL_DESTINATIONS.map(d => (
                                                <option key={d} value={d}>{d}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="space-y-1">
                                        <label className="flex items-center gap-2 text-[9px] font-black text-slate-400 uppercase tracking-widest ml-1">
                                            <StickyNote size={10} className="text-slate-300" /> Notes
                                        </label>
                                        <input 
                                            type="text" 
                                            value={item.notes || ''} 
                                            onChange={e => handleUpdateImport(idx, 'notes', e.target.value)}
                                            className="w-full bg-slate-50 border border-slate-200 focus:border-indigo-500 focus:bg-white focus:ring-4 focus:ring-indigo-500/10 outline-none text-slate-500 px-3 py-2 rounded-xl text-xs"
                                            placeholder="COMMENTS..."
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                 </div>

                 <div className="flex flex-col lg:flex-row gap-6 items-stretch pb-12 pt-2">
                    <div className="flex-1 bg-white p-6 rounded-[2rem] border border-slate-200 shadow-md flex flex-col justify-between relative overflow-hidden">
                        <div className="flex justify-between items-center mb-4 relative z-10">
                            <div>
                                <h5 className="font-black text-slate-400 uppercase tracking-widest text-[9px] mb-1">Total Workspace Aggregate</h5>
                                <p className="text-2xl font-black text-slate-900 tracking-tight">{totalQuantity.toLocaleString()} <span className="text-xs text-slate-300 uppercase">Metric Tons</span></p>
                            </div>
                            <div className="w-10 h-10 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center">
                                <ClipboardList size={20} />
                            </div>
                        </div>
                        <div className="flex gap-3 relative z-10">
                            <div className="px-3 py-1.5 bg-slate-50 border border-slate-100 rounded-lg text-[9px] font-black text-slate-500 uppercase tracking-widest">
                                {parsedData.imports.length} Entries
                            </div>
                            <div className="px-3 py-1.5 bg-emerald-50 border border-emerald-100 rounded-lg text-[9px] font-black text-emerald-600 uppercase tracking-widest">
                                Status: Validated
                            </div>
                        </div>
                        <div className="absolute top-0 right-0 w-48 h-48 bg-indigo-500/5 blur-3xl -mr-16 -mt-16"></div>
                    </div>

                    <div className="lg:w-80 bg-white p-6 rounded-[2rem] border border-slate-200 shadow-lg flex flex-col items-center text-center shrink-0 justify-center">
                         <h5 className="font-black text-slate-800 uppercase tracking-widest text-[10px] mb-2">Registry Commitment</h5>
                         <p className="text-slate-400 text-xs font-medium mb-4 leading-relaxed">Standardize entities before final archive.</p>
                         
                         <button 
                            onClick={() => setShowStandardizationModal(true)}
                            className="w-full py-3.5 bg-indigo-600 text-white rounded-2xl font-black text-[10px] uppercase tracking-[0.2em] hover:bg-indigo-700 transition-all active:scale-95 shadow-lg flex items-center justify-center gap-2"
                         >
                            Final Commit <ArrowRightCircle size={16} />
                         </button>
                    </div>
                 </div>
              </div>
          </div>
        </div>
      )}

      <datalist id="importer-suggestions">
          {suggestions.importers.map(s => <option key={s} value={s} />)}
      </datalist>
      <datalist id="supplier-suggestions">
          {suggestions.suppliers.map(s => <option key={s} value={s} />)}
      </datalist>
      <datalist id="notify-suggestions">
          {suggestions.notifyParties.map(s => <option key={s} value={s} />)}
      </datalist>
    </div>
  );
};

export default SmartEntry;